import React, { useEffect, useState, useMemo } from 'react';
import Loading from '$/components/loading/Loading';
import PrintPage from '$/components/printPage';
import { AnalyticsService, DocumentsClientService } from '$/services';
import * as Bixby from '$/core';
import NotFound from '$/pages/not-found';
import { CLIENT_COMPANY_DOCUMENTS, CLIENT_ORG_CHART_VIEW } from '$/core/constants';
import classnames from 'classnames';
import SortFilter from '$/components/sortFilter';
import { IFilterItem } from '$/components/sortFilter/filterItem';
import dayjs from 'dayjs';
import {
  FINANCIALS_CATEGORY,
  LENDER_PRESENTATIONS_CATEGORY,
  ORG_CHART_CATEGORY,
} from '$/components/company-documents-upload/categories';
import './index.scss';
import { orderBy } from '$/core/utils';
import BixButton from '$/components/global/bixButton';
import { PartialCompany } from './models';
import { ReactComponent as DownloadIcon } from '$/assets/icons/download.svg';
import { CompanyDocumentState } from '$/core/enums';

interface ICompanyDocumentsProps {
  currentCompany?: PartialCompany;
  profile?: Bixby.Models.Profile;
  featureFlags?: string[];
  isAdmin: boolean;
  isCurrentPortfolioCompanyPublic: boolean;
  getCompanyDocuments: (companyId: string) => Promise<Bixby.Models.CompanyDocument[]>;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
  dispatchDownloadFailure: () => Promise<void>;
  downloadAllDocuments: (companyId: string) => Promise<void>;
}

interface IDocumentItem {
  id: string;
  fileName: string;
  publishedDate: Date;
  title: string;
  description: string;
  currentCompany: PartialCompany;
  companyDocumentCompanyDocumentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[];
  className: string;
  clientName: string | undefined;
  isAdmin: boolean;
  handleDownload: (id: string) => void;
  featureFlags?: string[];
}

const categoriesWithExtras = [FINANCIALS_CATEGORY, LENDER_PRESENTATIONS_CATEGORY];

const DocumentItem: React.FC<IDocumentItem> = props => {
  const {
    id,
    publishedDate,
    title,
    description,
    currentCompany,
    companyDocumentCompanyDocumentCategories,
    className,
    handleDownload,
    isAdmin,
    clientName,
    featureFlags,
  } = props;

  const onDownload = async () => {
    await handleDownload(id);
  };

  const formatDate = (publishedDate: Date) => Bixby.Utils.utcDateToString(new Date(publishedDate));

  const formatCompanyDocumentCompanyDocumentCategories = () => {
    if (!Bixby.Utils.isNullOrEmpty(companyDocumentCompanyDocumentCategories)) {
      return companyDocumentCompanyDocumentCategories
        .map(cdcdc => {
          if (
            categoriesWithExtras.includes(cdcdc.companyDocumentCategory_Id) &&
            cdcdc.financialDocumentType &&
            cdcdc.financialDocumentDate
          ) {
            if (cdcdc.financialDocumentType === Bixby.Enums.FinancialDocumentType.Q) {
              let month = dayjs(cdcdc.financialDocumentDate).month();
              month += 1;
              let year = dayjs(cdcdc.financialDocumentDate).year();
              if (currentCompany?.fiscalYearEnd && currentCompany?.fiscalYearEnd !== null) {
                const fyeMonth = dayjs(currentCompany?.fiscalYearEnd).month() + 1;
                const fyeAdjustment = 12 - fyeMonth;
                if (month + fyeAdjustment > 12) {
                  month = month + fyeAdjustment - 12;
                  year = year + 1;
                } else {
                  month = month + fyeAdjustment;
                }
              }
              const quarter = month < 4 ? '1' : month >= 4 && month < 7 ? '2' : month >= 7 && month < 10 ? '3' : '4';
              return ` ${cdcdc.categoryName} (${cdcdc.financialDocumentType}${quarter}-${year
                .toString()
                .substring(2, 4)} - ${dayjs(cdcdc.financialDocumentDate).format('MMM-YY')})`;
            } else {
              return ` ${cdcdc.categoryName} (${cdcdc.financialDocumentType} - ${dayjs(
                cdcdc.financialDocumentDate
              ).format('MMM-YY')})`;
            }
          } else if (cdcdc.companyDocumentCategory_Id === ORG_CHART_CATEGORY && cdcdc.orgChartPageNumber) {
            if (featureFlags?.includes(CLIENT_ORG_CHART_VIEW)) {
              return `${cdcdc.categoryName} (Page ${cdcdc.orgChartPageNumber})`;
            }
          } else {
            return cdcdc.categoryName;
          }
        })
        .join('; ');
    }
  };

  return (
    <>
      <div className={className}>{formatDate(publishedDate)}</div>
      <div className={className}>{formatCompanyDocumentCompanyDocumentCategories()}</div>
      <div className={classnames('fake_link', className)} onClick={onDownload} role="button">
        {title}
      </div>
      <div className={className}>{description}</div>
      {isAdmin && <div>{clientName}</div>}
    </>
  );
};

interface IDocumentSectionProps {
  isAdmin: boolean;
  documents: Bixby.Models.CompanyDocument[];
  currentCompany: PartialCompany;
  handleDownload: (id: string) => void;
  featureFlags?: string[];
}

const DocumentSection: React.FC<IDocumentSectionProps> = props => {
  const { isAdmin, documents, currentCompany, handleDownload, featureFlags } = props;

  const formatDocTitle = (title: string, fileName: string) => {
    const extension = fileName.split('.').pop();
    return extension && extension.length > 0 ? `${title}.${extension}` : title;
  };

  return (
    <section className={classnames('documents_section', { 'has-extra-column': isAdmin })}>
      <div className="heading">Date Posted</div>
      <div className="heading">Type</div>
      <div className="heading">Document Title</div>
      <div className="heading">Bixby Description</div>
      {isAdmin && <div className="heading">Client (Admins Only)</div>}
      {documents?.map((document, ix) => {
        return (
          <DocumentItem
            key={ix}
            id={document.id}
            fileName={document.fileName}
            publishedDate={document.publishedDate}
            title={formatDocTitle(document.title, document.fileName)}
            description={document.description}
            currentCompany={currentCompany}
            companyDocumentCompanyDocumentCategories={document.companyDocumentCompanyDocumentCategories}
            handleDownload={handleDownload}
            className={classnames(ix % 2 === 0 ? 'even' : 'odd')}
            isAdmin={isAdmin}
            clientName={isAdmin ? document.client_Name : undefined}
            featureFlags={featureFlags}
          />
        );
      })}
    </section>
  );
};

const ClientCompanyDocumentsList: React.FC<ICompanyDocumentsProps> = props => {
  const {
    currentCompany,
    profile,
    dispatchDownloadDocument,
    isAdmin,
    isCurrentPortfolioCompanyPublic,
    getCompanyDocuments,
    dispatchDownloadFailure,
    featureFlags = [],
    downloadAllDocuments,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(undefined as string | undefined);
  const [documents, setDocuments] = useState([] as Bixby.Models.CompanyDocument[]);
  const [filters, setFilters] = useState([] as IFilterItem[]);
  const [activeFilters, setActiveFilters] = useState([] as string[]);
  const [categoryHighlight, setCategoryHighlight] = useState(undefined as string[] | undefined);
  const [financialDateHighlight, setFinancialDateHighlight] = useState(undefined as string[] | undefined);
  const [financialTypeHighlight, setFinancialTypeHighlight] = useState(undefined as string[] | undefined);
  const [hasHighlightWarning, setHasHighlightWarning] = useState(false);
  const [hasHighlightFilter, setHasHighlightFilter] = useState(false);
  const [fromFinancials, setFromFinancials] = useState(false);
  const [fromEmail, setFromEmail] = useState(false);
  const [idHighlight, setIdHighlight] = useState(undefined as string | undefined);

  const handleDownload = async (documentId: string) => {
    try {
      const { blob, filename } = await DocumentsClientService.downloadDocument(documentId);
      handleDocumentEvent(Bixby.Constants.APPINSIGHTS_DOCUMENTS_DOWNLOAD, documentId);
      dispatchDownloadDocument(blob, (filename || '').replace(/['"]/g, ''));
    } catch (err: any) {
      dispatchDownloadFailure();
    }
  };

  const handleDocumentEvent = (eventName: string, documentId: string) => {
    if (profile) {
      AnalyticsService.event(eventName, {
        ...profile,
        companyId: currentCompany?.id,
        companyName: currentCompany?.name,
        documentId,
      });
    }
  };

  const filteredDocuments = useMemo(() => {
    const checkDocumentHighlight = (document: Bixby.Models.CompanyDocument) => {
      // match docs where any of the categories match the category highlight
      const matchingCDCDCsByCDCId = categoryHighlight
        ? document.companyDocumentCompanyDocumentCategories.filter(cdcdc =>
            categoryHighlight.includes(cdcdc.companyDocumentCategory_Id.toLowerCase())
          )
        : [];
      if (!Bixby.Utils.isNullOrEmpty(matchingCDCDCsByCDCId)) {
        if (!financialDateHighlight && !financialTypeHighlight) {
          return true;
        } else {
          const matchingCDCDCsByFinancialDocumentTypes = financialTypeHighlight
            ? matchingCDCDCsByCDCId.filter(cdcdc =>
                cdcdc.financialDocumentType ? financialTypeHighlight.includes(cdcdc.financialDocumentType) : false
              )
            : [];
          if (!Bixby.Utils.isNullOrEmpty(matchingCDCDCsByFinancialDocumentTypes)) {
            if (financialDateHighlight && financialDateHighlight.length > 0) {
              return !Bixby.Utils.isNullOrEmpty(
                matchingCDCDCsByFinancialDocumentTypes.filter(cdcdc =>
                  financialDateHighlight.includes(dayjs.utc(cdcdc.financialDocumentDate).format('YYYY-MM-DD'))
                )
              );
            }
          }
        }
      } else if (idHighlight) {
        return document.id === idHighlight;
      }
      return false;
    };

    const filtered = (documents || []).filter(document => {
      if (idHighlight !== undefined && hasHighlightFilter) {
        return checkDocumentHighlight(document);
      } else if (hasHighlightFilter) {
        return checkDocumentHighlight(document);
      } else if (Bixby.Utils.isNullOrEmpty(activeFilters)) {
        return true;
      } else {
        return !Bixby.Utils.isNullOrEmpty(
          activeFilters.filter(filter =>
            document.companyDocumentCompanyDocumentCategories
              .map(cdcdc => cdcdc.companyDocumentCategory_Id.toLowerCase())
              .includes(filter)
          )
        );
      }
    });
    return filtered;
  }, [
    activeFilters,
    categoryHighlight,
    documents,
    financialDateHighlight,
    financialTypeHighlight,
    hasHighlightFilter,
    idHighlight,
  ]);

  useEffect(() => {
    if (!window.location.search) return;

    let hasWarning = true;
    const searchParams = new URLSearchParams(window.location.search);
    // Display the key/value pairs
    for (const [key, value] of searchParams.entries()) {
      if (key === 'category') {
        setCategoryHighlight(value.split(' ').map(_ => _.toLowerCase()));
        setHasHighlightFilter(true);
        setFromFinancials(true);
        hasWarning = false;
      } else if (key === 'type') {
        setFinancialTypeHighlight(value.split(' '));
        hasWarning = false;
      } else if (key === 'date') {
        setFinancialDateHighlight(value.split(' '));
        hasWarning = false;
      } else if (key === 'id') {
        setIdHighlight(value);
        setHasHighlightFilter(true);
        setFromEmail(true);
        hasWarning = false;
      }
    }
    setHasHighlightWarning(hasWarning);
  }, []);

  useEffect(() => {
    const getDocumentsAsync = async () => {
      if (!currentCompany) return;

      setIsLoading(true);
      setDocumentsError(undefined);
      try {
        const categories: Bixby.Models.CompanyDocumentCategory[] = await DocumentsClientService.getDocumentCategories();
        categories.push();
        setFilters(categories.map(({ id, name }) => ({ id, name } as IFilterItem)));

        const documents: Bixby.Models.CompanyDocument[] = await getCompanyDocuments(currentCompany.id);
        setDocuments(orderBy(documents, [_ => _.publishedDate, _ => _.title], ['desc', 'asc']));
      } catch (e: any) {
        setDocumentsError('Error retrieving documents');
      } finally {
        setIsLoading(false);
      }
    };

    if (
      featureFlags.includes(CLIENT_COMPANY_DOCUMENTS) &&
      !Bixby.Utils.isNullOrEmpty(currentCompany) &&
      isCurrentPortfolioCompanyPublic === false
    ) {
      getDocumentsAsync();
    }
  }, [featureFlags, currentCompany, isCurrentPortfolioCompanyPublic, getCompanyDocuments]);

  let feedbackUrl = '';

  if (currentCompany) {
    const companyNameSanitized: string = (currentCompany.name || '').replace('&', '%26');
    const dataSiteNameSanitized: string = (currentCompany.dataSiteName || '').replace('&', '%26');

    const feedbackUrlSubject = `Categorize%20Documents%20Request%20-%20${companyNameSanitized}`;
    const dataSite: string = dataSiteNameSanitized ? `%20%28${dataSiteNameSanitized}%29` : '';
    const feedbackUrlBody = `Please%20categorize%20documents%20for%20${companyNameSanitized}${dataSite}%2c%20and%20include%20any%20specific%20requests%20here%3A%0D%0A%0D%0A`;
    feedbackUrl = `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}&body=${feedbackUrlBody}`;
  }

  const handleFeedbackClick = () => {
    if (profile && currentCompany) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'Documents',
        companyName: currentCompany.name,
      });
    }
  };

  const handleFilter = (id: string, isFilterActive: boolean) => {
    if (isFilterActive) {
      setActiveFilters([...activeFilters, id]);
    } else {
      const indexOfFilter = activeFilters.findIndex(_ => _ === id);
      if (indexOfFilter > -1) {
        activeFilters.splice(indexOfFilter, 1);
        setActiveFilters([...activeFilters]);
      }
    }
  };

  const RemoveHighlight = () => {
    if (hasHighlightFilter) {
      setHasHighlightFilter(false);
    } else {
      setHasHighlightFilter(true);
    }
  };

  const handleClearFilters = () => void 0;

  const publishedDocuments = useMemo(() => {
    if (filteredDocuments === undefined) return;
    return filteredDocuments.filter(_ => _.state == CompanyDocumentState.Published);
  }, [filteredDocuments]);

  const deletedDocuments = useMemo(() => {
    if (filteredDocuments === undefined) return;
    return filteredDocuments.filter(_ => _.state == CompanyDocumentState.Deleted);
  }, [filteredDocuments]);

  if (!featureFlags.includes(CLIENT_COMPANY_DOCUMENTS)) {
    return <NotFound />;
  } else if (!Bixby.Utils.isNullOrEmpty(documentsError)) {
    return <p className="error">{documentsError}</p>;
  } else if (!currentCompany || Bixby.Utils.isNullOrEmpty(currentCompany)) {
    return null;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else if (isCurrentPortfolioCompanyPublic) {
    return (
      <div className="company_transcriptions">
        <p className="note_contain">
          Public company, for source docs visit the{' '}
          <a href={currentCompany.websiteUrl} target="_blank">
            investor relations site
          </a>
          , or{' '}
          <a href="mailto:feedback@bixbyresearch.com" role="button" onClick={handleFeedbackClick}>
            click here
          </a>{' '}
          to provide feedback to Bixby.
        </p>
      </div>
    );
  } else {
    return (
      <React.Fragment>
        <div className="documents_contain">
          <PrintPage />
          <section className="datasite_section">
            <dl>
              <dt>Data Site Provider:</dt>
              <dd>{currentCompany.dataSiteProviderName}</dd>
              <dt>Data Site Name:</dt>
              <dd>{currentCompany.dataSiteName}</dd>
              <dt>Bank:</dt>
              <dd>{currentCompany.dataSiteHostName}</dd>
            </dl>
            <div className="note">
              <p>
                Don't see documents below? &nbsp;
                <a href={feedbackUrl} role="button" onClick={handleFeedbackClick}>
                  Click here
                </a>
                &nbsp;to have Bixby upload information.
              </p>
            </div>
          </section>
          <section>
            {fromFinancials && filteredDocuments.length > 0 && (
              <div className="doc_source_heading">Source Doc View</div>
            )}
            <SortFilter filters={filters} onFilter={handleFilter} onClearFilters={handleClearFilters} />
            {(fromFinancials || fromEmail) && filteredDocuments.length > 0 && (
              <BixButton onClick={RemoveHighlight}> {hasHighlightFilter ? 'Show All Docs' : 'Go Back'} </BixButton>
            )}
          </section>
          {hasHighlightWarning && <p>Warning: no records found matching the pre-applied document filters.</p>}
          {filteredDocuments && filteredDocuments.length > 0 ? (
            <>
              <BixButton className="download_all" type="button" onClick={() => downloadAllDocuments(currentCompany.id)}>
                <>
                  <DownloadIcon />
                  Download All
                </>
              </BixButton>
              {publishedDocuments && publishedDocuments.length > 0 && (
                <div className="document_grid">
                  <h2>Published on Data Site</h2>
                  <DocumentSection
                    isAdmin={isAdmin}
                    documents={publishedDocuments}
                    currentCompany={currentCompany}
                    featureFlags={featureFlags}
                    handleDownload={handleDownload}
                  />
                </div>
              )}
              {deletedDocuments && deletedDocuments.length > 0 && (
                <>
                  <h2 className="title">Removed from Data Site</h2>
                  <DocumentSection
                    isAdmin={isAdmin}
                    documents={deletedDocuments}
                    currentCompany={currentCompany}
                    featureFlags={featureFlags}
                    handleDownload={handleDownload}
                  />
                </>
              )}
            </>
          ) : (
            'No docs available'
          )}
        </div>
      </React.Fragment>
    );
  }
};

export default ClientCompanyDocumentsList;
